import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Perfect Signs Canada",
    "url": "https://perfectsignscanada.com/contact.html",
    "description": "Contact Perfect Signs Canada for signage, websites, marketing, and POS support.",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Perfect Signs Canada",
      "telephone": "+1-780-554-3949",
      "email": "info@perfectsignscanada.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "6109 176 Ave NW",
        "addressLocality": "Edmonton",
        "addressRegion": "AB",
        "postalCode": "T5Y 0K8",
        "addressCountry": "CA"
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus({ submitting: false, submitted: false, error: null });

    // Validate name: At least 2 letters
    const namere = /[A-Za-z]{1}[A-Za-z]/;
    if (!namere.test(formData.name)) {
      setStatus({ submitting: false, submitted: false, error: "Please enter a name with at least 2 letters." });
      return;
    }

    // Validate phone: 10 to 20 digits
    const mobilere = /^[0-9+\-\s()]{10,20}$/;
    if (!mobilere.test(formData.phone)) {
      setStatus({ submitting: false, submitted: false, error: "Please enter a valid phone number." });
      return;
    }

    // Validate email
    const reeamil = /^([\w-.]+@([\w-]+\.)+[\w-]{2,})$/;
    if (!reeamil.test(formData.email)) {
      setStatus({ submitting: false, submitted: false, error: "Please enter a valid email address." });
      return;
    }

    // Validate service select
    if (!formData.service) {
      setStatus({ submitting: false, submitted: false, error: "Please select what you need help with." });
      return;
    }

    setStatus({ submitting: true, submitted: false, error: null });

    const payload = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      service: formData.service,
      message: formData.message
    };

    // Post to their exact API endpoint
    fetch("https://wgcsl78a5m.execute-api.ca-central-1.amazonaws.com/Prod/api/PerfectSigns", {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8"
      },
      body: JSON.stringify(payload)
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Submission API call failed");
        }
        setStatus({
          submitting: false,
          submitted: true,
          error: null
        });
        // Clear form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      })
      .catch((err) => {
        console.error(err);
        setStatus({
          submitting: false,
          submitted: false,
          error: "Sorry, the message could not be sent right now. Please call us or email info@perfectsignscanada.com."
        });
      });
  };

  return (
    <>
      <SEO 
        title="Contact Perfect Signs Canada | Signs, Websites, Marketing & POS"
        description="Contact Perfect Signs Canada for custom signage, vehicle wraps, banners, websites, SEO, marketing, hosting, POS systems, and business technology support."
        keywords="contact Perfect Signs Canada, sign company Edmonton, custom signs quote, website design quote, SEO services, POS systems, vehicle wraps, business signs Canada"
        canonical="https://perfectsignscanada.com/contact.html"
        schema={contactSchema}
      />

      {/* ======= Breadcrumbs ======= */}
      <div className="breadcrumbs d-flex align-items-center" style={{ backgroundImage: "url('/assets/img/contact-header.jpg')" }}>
        <div className="container position-relative d-flex flex-column align-items-center" data-aos="fade-up">
          <p className="breadcrumbs-eyebrow">Contact Perfect Signs Canada</p>
          <h2>Tell us what you want people to notice first.</h2>
          <p className="breadcrumbs-summary">Request a quote for signage, wraps, websites, SEO, marketing, POS systems, or business technology support.</p>
          <ol>
            <li><Link to="/">Home</Link></li>
            <li>Contact</li>
          </ol>
        </div>
      </div>
      {/* End Breadcrumbs */}

      {/* ======= Contact Section ======= */}
      <section id="contact" className="contact">
        <div className="container position-relative" data-aos="fade-up">

          <div className="contact-intro" data-aos="fade-up">
            <p className="section-kicker">Start a project</p>
            <h2>Share a few details and we’ll help you choose the right next step.</h2>
            <p>Whether you need a sign, vehicle wrap, storefront graphics, website, marketing help, or POS support, send us a message and we’ll get back to you as soon as possible.</p>
          </div>

          <div className="row gy-4 d-flex justify-content-between">
            {/* Info Item Details */}
            <div className="col-lg-5" data-aos="fade-up" data-aos-delay="100">
              <div className="info-item d-flex">
                <i className="bi bi-geo-alt flex-shrink-0"></i>
                <div>
                  <h4>Location</h4>
                  <p>6109 176 Ave NW, Edmonton, AB T5Y 0K8 CANADA</p>
                </div>
              </div>

              <div className="info-item d-flex">
                <i className="bi bi-envelope flex-shrink-0"></i>
                <div>
                  <h4>Email</h4>
                  <p><a href="mailto:info@perfectsignscanada.com">info@perfectsignscanada.com</a></p>
                </div>
              </div>

              <div className="info-item d-flex">
                <i className="bi bi-phone flex-shrink-0"></i>
                <div>
                  <h4>Call</h4>
                  <p><a href="tel:+17805543949">+1 780-554-3949</a></p>
                </div>
              </div>

              <div className="contact-note">
                <strong>Helpful details to include</strong>
                <span>Project type, location, timeline, approximate size, and any photos or examples you already have.</span>
              </div>
            </div>

            {/* Form Inputs Panel */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="250">
              <form id="contact-form" onSubmit={handleSubmit} className="contact-form-panel">
                <div className="row">
                  <div className="col-md-6 form-group">
                    <input 
                      type="text" 
                      name="name" 
                      className="form-control" 
                      id="name-input" 
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Name" 
                      required 
                    />
                  </div>
                  <div className="col-md-6 form-group mt-3 mt-md-0">
                    <input 
                      type="email" 
                      className="form-control" 
                      name="email" 
                      id="email-input" 
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Your Email" 
                      required 
                    />
                  </div>
                </div>
                <div className="form-group mt-3">
                  <input 
                    type="tel" 
                    className="form-control" 
                    name="phone" 
                    id="phone-input" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    required 
                  />
                </div>
                <div className="form-group mt-3">
                  <select 
                    className="form-control" 
                    name="service" 
                    id="service-input" 
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">What can we help with?</option>
                    <option value="Custom Signage">Custom Signage</option>
                    <option value="Vehicle Wraps">Vehicle Wraps</option>
                    <option value="Website or IT Services">Website or IT Services</option>
                    <option value="Marketing">Marketing</option>
                    <option value="POS Support">POS Support</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group mt-3">
                  <textarea 
                    className="form-control" 
                    name="message" 
                    id="message-input" 
                    rows="5" 
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project" 
                    required 
                  />
                </div>
                <div className="my-3">
                  {status.submitting && <div className="loading d-block">Sending your message...</div>}
                  {status.error && <div className="error-message d-block">{status.error}</div>}
                  {status.submitted && <div className="sent-message d-block">Thank you. Your request has been sent and we will get back to you as soon as possible.</div>}
                </div>
                <div className="text-center mt-3">
                  <button type="submit" className="contact-submit" disabled={status.submitting}>
                    {status.submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
